/* eslint-disable max-statements */
/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    ContinueWatchingItem,
    StreamUrlResponse
} from '@24i/st-api-connector';
import { getStreamOptionsByType } from 'Utils';
import type { PlayerData, StreamData, StreamType } from 'Types';
import { useStore } from 'Store';
import { logEvent } from 'AppManager';
import { ConfirmationModal, useConfirmationModal } from 'LocalGluons';
import { isUserFreemium } from 'Utils/userUtils';
import { t } from 'Locale';
import NavigationService from 'NavigationService';
import { getOrientation } from 'DeviceUtils';
import { STREAM_TYPE } from 'Options';
import { SeekType } from '@24i/player-base';
import { getStreamTypeByItemType } from '../playerUtils';
import { checkContinueWatching, shouldHaveContinueWatching } from '../playerContainerUtils';
import { sendEndEvent } from '../components/ContinueWatchingPlayer/continueWatchingUtils';
import DeviceLimitDialog from '../components/DeviceLimitDialog';
import { useNavigationPropsContext } from '../../../../runtime/Navigation/navigationPropsContext';
import { SystemInfoHelper } from '../../../../AppManager/helpers/SystemInfoHelper';
import useMirroringCheck from './useMirroringCheck';
import { useChromecastContext } from 'LocalGluons/components/ChromecastContext';

const useStreamData = (playerData: PlayerData, disableContinueWatching = false) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { navigate } = useNavigationPropsContext();
    const { setModalContent } = useConfirmationModal();
    const { client, setMediaState } = useChromecastContext()
    const {
        appManager,
        deviceStorageState,
        setPlayerData,
        userData,
        continueWatchingEnabled
    } = useStore();

    const navigation = NavigationService.getAppNavigationInstance();

    const {
        item,
        isTrailer,
        offline,
        streamUrl,
        shouldRewindToStart,
        isLiveRecording
    } = playerData;
    const [itemStreamData, setItemStreamData] = useState<StreamData | null>(null);
    const [continueWatchingData, setContinueWatchingData] = useState<ContinueWatchingItem | null>(
        null
    );
    const [isLoading, setLoading] = useState(false);
    const [modalDisplayed, setModalDisplayed] = useState(false);
    const { isMirroring } = useMirroringCheck();
    const lastStreamUrlParams = useRef<string>('');

    const closeDialog = useCallback(() => {
        setModalContent(null);
        setPlayerData(null);
    }, [setModalContent, setPlayerData]);

    const goToDevices = useCallback(() => {
        closeDialog();
        navigation.navigate('DeviceManagement', { playerData });
    }, [closeDialog, navigation, playerData]);

    const getStreamData = useCallback(
        async (
            startTime = 0.0,
            customStreamType?: StreamType,
            startPositionType: SeekType = SeekType.TIMESHIFT
        ) => {
            if (modalDisplayed) setModalDisplayed(false);
            const streamType = customStreamType || getStreamTypeByItemType(item);

            let wasSuccessful = true;
            const options = {
                ...(await getStreamOptionsByType(
                    item,
                    streamType,
                    deviceStorageState.videoQualityProfile
                )),
                trailer: isTrailer,
                startTime
            };

            const newUrlParams = JSON.stringify(options);
            // If last params are the same, do not download new data. return success
            if (!client && (lastStreamUrlParams.current === newUrlParams || isLiveRecording)) {
                setLoading(false);
                return wasSuccessful;
            }
            lastStreamUrlParams.current = newUrlParams;
            if (!isMirroring) {
                try {
                    const streamData: StreamUrlResponse = await appManager.api.fetchStreamUrl(options);
                    const config = await appManager.getMCoreConfigData();
                    const drmLicenseUrl = (config?.widevineDrmUrl && streamData?.ecid) && `${config.widevineDrmUrl}${btoa(streamData.ecid)}`
                    setItemStreamData({
                        streamUrl: streamData.url,
                        streamType: options.service,
                        streamData,
                        // this si hack to ensure correct behavior or synthetic channels
                        startTime:
                            (options.service === STREAM_TYPE.LIVE
                                && streamData.continueWatchingInfo !== null)
                                ? streamData.continueWatchingInfo.stopTimeInMillis
                                : startTime,
                        // timeshift is in miliseconds
                        maxTimeshift: streamData.timeshift * 1000,
                        startPositionType,
                        drmLicenseUrl: drmLicenseUrl
                    });
                    // lockToLandscape();
                } catch (err) {
                    const error = err as { errorCode: string };

                    console.log('in useStreamData.tsx: getStreamData chatch>        ', error);
                    const orientation = await getOrientation();
                    const isLandscape = orientation.includes('LANDSCAPE');
                    if (
                        error.errorCode === 'DEVICE_MAX_LIMIT' ||
                        error.errorCode === 'BENEFIT_DEVICE_MAX_LIMIT'
                    ) {
                        const systemInfo = new SystemInfoHelper();
                        await systemInfo.load();
                        logEvent({
                            type: 'error',
                            subtype: 'player',
                            error: 'deviceLimitReached',
                            context: 'Player',
                            ...systemInfo.get()
                        });

                        setModalDisplayed(true);
                        setModalContent(
                            <DeviceLimitDialog
                                close={closeDialog}
                                playerData={playerData}// goToDevices={goToDevices}
                                isLandscape={isLandscape}
                                isFreemium={
                                    isUserFreemium(userData) &&
                                    error.errorCode === 'BENEFIT_DEVICE_MAX_LIMIT'
                                }
                            />,
                            () => setPlayerData(null)
                        );
                    } else if (error.errorCode === 'NO_LOGGED') {
                        setModalDisplayed(true);
                        setModalContent(
                            <ConfirmationModal
                                icon="warning_yellow_black"
                                header={t('NOT_LOGGED_IN_MSG')}
                                message={t('NOT_LOGGED_IN_TXT')}
                                confirmLabel={t('COMMON.LOGIN')}
                                cancelLabel={t('COMMON.CANCEL')}
                                onConfirm={() => {
                                    navigate('Login');
                                    closeDialog();
                                }}
                                onCancel={closeDialog}
                                onClose={closeDialog}
                                mode={isLandscape ? 'landscape' : null}
                            />
                        );
                    } else if (error.errorCode === 'BENEFIT_NO_PACKAGE' && isUserFreemium(userData)) {
                        setModalDisplayed(true);
                        setModalContent(
                            <ConfirmationModal
                                icon="warning_yellow_black"
                                header={t('LIVE.UPGRADE_PACKAGE_TITLE___PACKAGE.BENEFIT')}
                                message={t('LIVE.UPGRADE_PACKAGE_DESCRIPTION___PACKAGE.BENEFIT')}
                                onCancel={closeDialog}
                                onClose={closeDialog}
                                mode={isLandscape ? 'landscape' : null}
                            />
                        );
                        // } else if (!playerIsOpened.current) {
                        //     setError({ error, screenId: 'player' });
                        //     onClose();
                        // } else {
                        //     setPlayerAlertMessage(getErrorMessage(error));
                        //     setItem(currentItem.current);

                    } else if (error.errorCode === 'NO_PACKAGE') {
                        setModalDisplayed(true);
                        setModalContent(
                            <ConfirmationModal
                                icon="warning_yellow_black"
                                header={t('COMMON.INFORMATION_MSG')}
                                message={err.errorMessage}
                                onCancel={closeDialog}
                                onClose={closeDialog}
                                mode={isLandscape ? 'landscape' : null}
                                confirmLabel={t('COMMON.OK')}
                                onConfirm={() => {
                                    closeDialog();
                                }}
                            />
                        );
                    } else if (error.errorCode === 'DUMMY_SCHEDULE') {
                        setModalDisplayed(true);
                        setModalContent(
                            <ConfirmationModal
                                icon="warning_yellow_black"
                                header={t('COMMON.INFORMATION_MSG')}
                                message={err.errorMessage}
                                onCancel={closeDialog}
                                onClose={closeDialog}
                                mode={isLandscape ? 'landscape' : null}
                                confirmLabel={t('COMMON.OK')}
                                onConfirm={() => {
                                    closeDialog();
                                }}
                            />
                        );
                    } else {
                        setModalDisplayed(true);
                        setModalContent(
                            <ConfirmationModal
                                icon="warning_yellow_black"
                                header={t('COMMON.INFORMATION_MSG')}
                                message={err.errorMessage}
                                onCancel={closeDialog}
                                onClose={closeDialog}
                                mode={isLandscape ? 'landscape' : null}
                                confirmLabel={t('COMMON.OK')}
                                onConfirm={() => {
                                    closeDialog();
                                }}
                            />
                        );
                    }
                    wasSuccessful = false;
                }
            }
            setLoading(false);
            return wasSuccessful;
        },
        [
            appManager.api,
            closeDialog,
            deviceStorageState.videoQualityProfile,
            goToDevices,
            isTrailer,
            item,
            modalDisplayed,
            navigation,
            setModalContent,
            setPlayerData,
            shouldRewindToStart,
            userData
        ]
    );

    const onContinueWatching = useCallback(async () => {
        if (client) {
            setMediaState((state) => ({
                ...state,
                isPaused: false
            }))
        }
        const startTime = continueWatchingData ? continueWatchingData.stopTimeInMillis : 0;
        try {
            await getStreamData(startTime, undefined, SeekType.CONTENT_TIME);
        } catch (error) {
            console.error('in useStreamData.tsx: o nContinueWatching>       ', error);
            await getStreamData();
        }
        setContinueWatchingData(null);
    }, [continueWatchingData, getStreamData]);

    const onRestart = useCallback(() => {
        if (client) {
            setMediaState((state) => ({
                ...state,
                isPaused: false
            }))
        }
        sendEndEvent(item.modelType, item.getId());
        getStreamData(0, undefined, SeekType.CONTENT_TIME);
        setContinueWatchingData(null);
    }, [getStreamData, item]);

    // If item is changed, get new stream data for that item
    useEffect(() => {
        const fetchFunc = async () => {
            // if (!streamUrl) return;
            setLoading(true);
            if (shouldHaveContinueWatching(
                item, offline, isTrailer
            ) && continueWatchingEnabled) {
                try {
                    const data = await checkContinueWatching(item);
                    if (data && data.stopTimeInMillis > 30000) setContinueWatchingData(data);
                    await getStreamData();
                } catch (error) {
                    console.error('in useStreamData.tsx: fetch Error>        ', error);
                    await getStreamData();
                }
            } else if (offline) {
                // setItemStreamData({ streamUrl, offline });
                setLoading(false);
            } else if (shouldRewindToStart) {
                await getStreamData(0, STREAM_TYPE.ARCHIVE, SeekType.UTC);
            } else {
                await getStreamData();
            }
        };
        fetchFunc();
        // useEffect should not deppend on the getStreamData callback
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTrailer, item, offline, shouldRewindToStart, streamUrl, playerData]);

    return {
        itemStreamData,
        isLoading,
        continueWatchingData,
        onContinueWatching,
        onRestart,
        getStreamData,
        modalDisplayed
    };
};

export default useStreamData;
